import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button , Input, Card, FileUpload, ResponsiveTable, ActionDropdown } from '@shared';
import { FileText, File, Table, Presentation, Upload, Search, Filter, Loader2, X, AlertTriangle, CheckCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import api from '@/utils/api';
import DocumentDetailsModal from '@/components/documents/DocumentDetailsModal';
import DeleteConfirmModal from '@/components/documents/DeleteConfirmModal';
import FileIcon from '@/components/documents/FileIcon';
import StatusBadge from '@/components/documents/StatusBadge';
import { formatRelative } from 'date-fns';
import { useAuth } from '@/components/auth/AuthContext';
import { toast } from 'react-hot-toast';
import { env } from '@/config/env.ts';

// --- Default Constants and Helpers for Upload Component (fallback values) ---
const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const DEFAULT_POLLING_INTERVAL_MS = 3000; // Poll every 3 seconds
const DEFAULT_MAX_POLLS_MINUTES = 5; // 5 minutes total

const DEFAULT_SUPPORTED_FORMATS_EXTENSIONS = ['pdf', 'docx', 'doc', 'txt', 'md', 'xls', 'xlsx'];

interface FormatDetails {
  extension: string;
  mime: string;
  icon: LucideIcon;
  color: string;
}

interface Document {
  _id: string;
  id: string;
  originalName: string;
  fileType: string;
  fileSize: number;
  pages?: number;
  uploadedAt: string;
  status: string;
  queryCount?: number;
  [key: string]: any;
}

interface FileObj {
  file: File;
  errors: string[];
  id: string;
}

interface UploadProgress {
  [id: string]: number;
}

interface UploadStatus {
  [id: string]: 'uploading' | 'processing' | 'ready' | 'failed';
}

interface SupportedFormats {
  [mime: string]: FormatDetails;
}

// Helper to map file extensions to MIME types and icons/colors
const getFormatDetails = (extension: string): FormatDetails => {
  switch (extension.toLowerCase()) {
    case 'pdf': return { extension: 'PDF', mime: 'application/pdf', icon: FileText, color: 'text-red-500' };
    case 'docx': return { extension: 'DOCX', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', icon: FileText, color: 'text-blue-500' };
    // case 'doc': return { extension: 'DOC', mime: 'application/msword', icon: FileText, color: 'text-blue-500' };
    case 'txt': return { extension: 'TXT', mime: 'text/plain', icon: File, color: 'text-gray-500' };
    case 'md': return { extension: 'MD', mime: 'text/markdown', icon: File, color: 'text-purple-500' };
    // case 'xls': return { extension: 'XLS', mime: 'application/vnd.ms-excel', icon: Table, color: 'text-green-500' };
    case 'xlsx': return { extension: 'XLSX', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', icon: Table, color: 'text-green-500' };
    case 'pptx': return { extension: 'PPTX', mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', icon: Presentation, color: 'text-orange-500' };
    default: return { extension: extension.toUpperCase(), mime: 'application/octet-stream', icon: File, color: 'text-gray-500' };
  }
};


const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const Documents: React.FC = () => {
  const { } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // --- State for embedded upload component ---
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileObj[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({}); // { fileName: percentage }
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({}); // { fileName: 'uploading' | 'processing' | 'complete' | 'failed' } // Changed to a mutable object for direct updates
  const pollingIntervalsRef = useRef<Record<string, NodeJS.Timeout>>({}); // To store interval IDs for each file
  // --- End State for embedded upload component ---

  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [processingDocs, setProcessingDocs] = useState(new Set<string>()); // This needs to be a mutable state that can trigger re-renders
  const [pollIntervalId, setPollIntervalId] = useState<NodeJS.Timeout | null>(null);

  // State to manage which dropdown is open
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);


  // Read configurations from .env with fallback logic
  const MAX_FILE_SIZE = useMemo(() => {
    const size = env.fileUpload.maxFileSizeMB;
    return !isNaN(size) && size > 0 ? size * 1024 * 1024 : DEFAULT_MAX_FILE_SIZE;
  }, []);

  const SUPPORTED_FORMATS = useMemo((): SupportedFormats => {
    const envTypes = env.fileUpload.supportedTypes;
    const extensions = envTypes ? envTypes.filter((ext: string) => ext) : DEFAULT_SUPPORTED_FORMATS_EXTENSIONS;
    const formats: SupportedFormats = {};
    const mimeTypes = new Set<string>();
    extensions.forEach((ext: string) => {
      const details = getFormatDetails(ext);
      // Prevent duplicate MIME types if different extensions map to the same MIME
      if (!mimeTypes.has(details.mime)) {
        formats[details.mime] = details;
        mimeTypes.add(details.mime);
      }
    });
    return formats;
  }, []);
  
  const POLLING_INTERVAL_MS = useMemo(() => {
    const interval = env.fileUpload.pollingIntervalMs;
    return !isNaN(interval) && interval > 0 ? interval : DEFAULT_POLLING_INTERVAL_MS;
  }, []);

  const MAX_POLLS = useMemo(() => {
    const minutes = env.fileUpload.maxPollsMinutes;
    const totalMs = !isNaN(minutes) && minutes > 0 ? minutes * 60 * 1000 : DEFAULT_MAX_POLLS_MINUTES * 60 * 1000;
    return Math.floor(totalMs / POLLING_INTERVAL_MS);
  }, [POLLING_INTERVAL_MS]);


  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page,
        limit: 10,
        search: searchQuery,
        status: statusFilter !== 'all' ? statusFilter : '',
      };
      const response = await api.getDocuments(params);
      setDocuments(response.documents);
      setTotalPages(response.totalPages);

      // Identify documents that are currently processing
      const currentlyProcessing = new Set<string>(
        response.documents
          .filter((doc: Document) => doc.status === 'processing')
          .map((doc: Document) => doc._id)
      );
      setProcessingDocs(currentlyProcessing);

    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter]);

  // Polling for processing documents in the main list
  useEffect(() => {
    if (processingDocs.size > 0) {
      if (!pollIntervalId) {
        const id = setInterval(() => {
          fetchDocuments();
        }, POLLING_INTERVAL_MS); // Use configurable polling interval
        setPollIntervalId(id);
      }
    } else if (pollIntervalId) {
      clearInterval(pollIntervalId);
      setPollIntervalId(null);
    }
    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
    };
  }, [processingDocs, fetchDocuments, pollIntervalId, POLLING_INTERVAL_MS]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments, page, searchQuery, statusFilter]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1); // Reset to first page on filter change
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const openDocumentDetails = (document: Document) => {
    setSelectedDocument(document);
    setOpenDropdownId(null); // Close dropdown when opening details
  };

  const closeDocumentDetails = () => {
    setSelectedDocument(null);
  };

  const handleDeleteClick = (document: Document) => {
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
    setOpenDropdownId(null); // Close dropdown when opening delete confirm
  };

  const confirmDelete = async (deletePhysicalFile: boolean) => {
      if (!documentToDelete) return;
    try {
      await api.deleteDocument(documentToDelete._id, { deletePhysicalFile });
      toast.success('Document deleted successfully.');
      fetchDocuments(); // Refresh the list
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document.');
    }
  };

  const handleReprocess = async (documentId: string) => {
    try {
      toast.loading('Reprocessing document...', { id: 'reprocess-toast' });
      await api.reprocessDocument(documentId);
      toast.success('Document reprocessing started.', { id: 'reprocess-toast' });
      fetchDocuments(); // Refresh to show "processing" status
      setOpenDropdownId(null); // Close dropdown
    } catch (error: any) {
      console.error('Failed to reprocess document:', error);
      toast.error(`Failed to reprocess: ${error.message}`, { id: 'reprocess-toast' });
    }
  };

  // Handler for secure document download
  const handleDownload = async (document: Document) => {

    if (!document?._id || !document?.originalName) {
      toast.error('Document information missing for download.');
      return;
    }

    try {
      toast.loading('Preparing document for download...', { id: 'downloadToast' });
      // Use api.downloadDocument which is designed to handle blob responses
      const blob = await api.downloadDocument(document._id);

      if (!blob) {
        throw new Error('No file content received.');
      }

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.originalName; // Use the original file name
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url); // Clean up the object URL

      toast.success('Document downloaded successfully!', { id: 'downloadToast' });
    } catch (error: any) {
      console.error('Failed to download document:', error);
      toast.error(`Failed to download document: ${error.message || 'Network error.'}`, { id: 'downloadToast' });
    }
  };

  // Toggle dropdown visibility
  const toggleDropdown = useCallback((docId: string) => {
    setOpenDropdownId(openDropdownId === docId ? null : docId);
  }, [openDropdownId]);

  // Removed useEffect for handleClickOutside as ActionDropdown handles it internally
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
  //       setOpenDropdownId(null);
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [dropdownRef]);


  // Memoized empty state component
  const EmptyState: React.FC<{ icon: LucideIcon; title: string; description: string; action?: { onClick: () => void; label: string } }> = ({ icon: Icon, title, description, action }) => (
    <div className="text-center py-12">
      <Icon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {title}
      </h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        {description}
      </p>
      {action && (
        <Button onClick={action.onClick} variant="primary">
          {action.label}
        </Button>
      )}
    </div>
  );

  // --- Handlers for embedded upload component (moved from DocumentUploadModal.jsx) ---
  const handleFilesSelected = useCallback((files: File[]) => {
    const newFiles: FileObj[] = files.map(file => ({
      file,
      errors: [], // FileUpload component already validated these
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`
    }));
    setSelectedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleFileRemove = useCallback((file: File) => {
    setSelectedFiles(prev => {
      const fileObj = prev.find(f => f.file === file);
      if (fileObj) {
        // Clear any active polling for this file
        if (pollingIntervalsRef.current[fileObj.id]) {
          clearInterval(pollingIntervalsRef.current[fileObj.id]);
          delete pollingIntervalsRef.current[fileObj.id];
        }
        // Remove from progress and status
        setUploadProgress(p => {
          const newProgress = { ...p };
          delete newProgress[fileObj.id];
          return newProgress;
        });
        setUploadStatus(s => {
          const newStatus = { ...s };
          delete newStatus[fileObj.id];
          return newStatus;
        });
      }
      return prev.filter(f => f.file !== file);
    });
  }, []);

  const removeFile = useCallback((id: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[id];
      return newProgress;
    });
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[id];
      return newStatus;
    });
    // Clear any active polling for this file
    if (pollingIntervalsRef.current[id]) {
      clearInterval(pollingIntervalsRef.current[id]);
      delete pollingIntervalsRef.current[id];
    }
  }, []);

  const uploadFile = useCallback(async (fileObj: FileObj) => {
    const { file, id } = fileObj;
    setUploadProgress(prev => ({ ...prev, [id]: 0 }));
    setUploadStatus(prev => ({ ...prev, [id]: 'uploading' }));

    let pollCount = 0;

    try {
      const uploadResult = await api.uploadDocument(file, (progress: number) => {
        setUploadProgress(prev => ({ ...prev, [id]: Math.round(progress) }));
      });

      setUploadStatus(prev => ({ ...prev, [id]: 'processing' }));
      toast.success(`'${file.name}' uploaded. Processing started.`, { id: `upload-${id}` });
      setUploadProgress(prev => ({ ...prev }));

      const pollInterval = setInterval(async () => {
        pollCount++;
        if (pollCount > MAX_POLLS) {
          clearInterval(pollInterval);
          delete pollingIntervalsRef.current[id];
          setUploadStatus(prev => ({ ...prev, [id]: 'failed' }));
          toast.error(`'${file.name}' processing timed out. Please check backend logs.`, { id: `upload-${id}` });
          fetchDocuments();
          setUploadProgress(prev => ({ ...prev }));
          return;
        }

        try {
          const doc = await api.getDocument(uploadResult.documentId);
          if (doc.status === 'ready' || doc.status === 'failed') {
            clearInterval(pollInterval);
            delete pollingIntervalsRef.current[id];
            setUploadStatus(prev => ({ ...prev, [id]: doc.status as 'ready' | 'failed' }));
            if (doc.status === 'ready') {
              toast.success(`'${file.name}' processed successfully.`, { id: `upload-${id}` });
            } else {
              toast.error(`'${file.name}' processing failed: ${doc.errorMessage || 'Unknown error'}`, { id: `upload-${id}` });
            }
            fetchDocuments();
            setUploadProgress(prev => ({ ...prev }));
          }
        } catch (pollError) {
          console.error(`Polling error for ${file.name}:`, pollError);
          clearInterval(pollInterval);
          delete pollingIntervalsRef.current[id];
          setUploadStatus(prev => ({ ...prev, [id]: 'failed' }));
          toast.error(`'${file.name}' processing status check failed.`, { id: `upload-${id}` });
          fetchDocuments();
          setUploadProgress(prev => ({ ...prev }));
        }
      }, POLLING_INTERVAL_MS);
      pollingIntervalsRef.current[id] = pollInterval;

    } catch (error: any) {
      setUploadStatus(prev => ({ ...prev, [id]: 'failed' }));
      setUploadProgress(prev => ({ ...prev, [id]: 0 }));
      toast.error(`Upload of '${file.name}' failed: ${error.message}`, { id: `upload-${id}` });
    }
  }, [fetchDocuments, setUploadStatus, POLLING_INTERVAL_MS, MAX_POLLS]);

  const handleUploadAll = async () => {
    setUploading(true);
    const filesToUpload = selectedFiles.filter(f => f.errors.length === 0 && !uploadStatus[f.id]);
    for (const fileObj of filesToUpload) {
      await uploadFile(fileObj); 
    }
    setUploading(false);
  };

  // Clear all polling intervals on component unmount
  useEffect(() => {
    return () => {
      for (const id in pollingIntervalsRef.current) {
        clearInterval(pollingIntervalsRef.current[id]);
      }
    };
  }, []);


  const allFilesValid = selectedFiles.every(f => f.errors.length === 0);
  const hasFilesToUpload = selectedFiles.some(f => !uploadStatus[f.id]);
  // Check if any file is explicitly 'uploading' or 'processing'
  const isAnyFileUploadingOrProcessing = selectedFiles.some(f => uploadStatus[f.id] === 'uploading' || uploadStatus[f.id] === 'processing');

  const resetUploadSection = () => {
    // Clear all polling intervals for selected files before resetting
    for (const fileObj of selectedFiles) {
      if (pollingIntervalsRef.current[fileObj.id]) {
        clearInterval(pollingIntervalsRef.current[fileObj.id]);
        delete pollingIntervalsRef.current[fileObj.id];
      }
    }
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadStatus({});
    setUploading(false);
    setShowUploadSection(false);
  };
  // --- End Handlers for embedded upload component ---

  // Helper to truncate filename for display
  const truncateFilename = (filename: string, maxLength: number = 40): string => { // Increased maxLength to 40 for better readability
    if (filename.length <= maxLength) {
      return filename;
    }
    return filename.substring(0, maxLength - 3) + '...';
  };

  // --- Column definitions for ResponsiveTable (Document List) ---
  const documentListColumns = useMemo(() => [
    {
      key: 'originalName',
      header: 'Filename',
      render: (originalName: any, doc: Document) => (
        <Button variant="secondary" onClick={() => openDocumentDetails(doc)} className="font-medium text-primary-500 cursor-pointer hover:underline max-w-xs truncate">
          <span title={originalName}>{truncateFilename(originalName)}</span>
        </Button>
      ),
    },
    {
      key: 'fileType',
      header: 'Type',
      render: (fileType: any, _doc: Document) => <FileIcon fileType={fileType} />,
    },
    {
      key: 'fileSize',
      header: 'Size',
      render: (fileSize: any, _doc: Document) => formatFileSize(fileSize),
    },
    /*{
      key: 'pages',
      header: 'Pages',
      render: (pages) => pages || '-',
    },*/
    {
      key: 'status',
      header: 'Status',
      render: (status: any, _doc: Document) => <StatusBadge status={status} />,
    },
    {
      key: 'uploadedAt',
      header: 'Uploaded',
      render: (uploadedAt: any, _doc: Document) => formatRelative(new Date(uploadedAt), new Date()),
    },
    /*{
      key: 'processingCompletedAt',
      header: 'Processed',
      render: (processingCompletedAt) => formatRelative(new Date(processingCompletedAt), new Date()),
    },
    */
    {
      key: 'actions',
      header: 'Actions',
      render: (_value: any, doc: Document) => (
        <ActionDropdown
          isOpen={openDropdownId === doc._id}
          onToggle={() => toggleDropdown(doc._id)}
        >
          <button onClick={() => openDocumentDetails(doc)} className="text-gray-700 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100" role="menuitem">View Details</button>
          <button onClick={() => handleDownload(doc)} className="text-gray-700 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100" role="menuitem">Download</button>
          {(doc.status === 'failed' || doc.status === 'ready') && ( /* Added condition for 'ready' to allow reprocessing ready docs */
              <button onClick={() => handleReprocess(doc._id)} className="text-gray-700 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100" role="menuitem">Reprocess</button>
          )}
          <button onClick={() => handleDeleteClick(doc)} className="text-red-600 block px-4 py-2 text-sm w-full text-left hover:bg-red-50" role="menuitem">Delete</button>
        </ActionDropdown>
      ),
    },
  ], [openDropdownId, toggleDropdown, openDocumentDetails, handleDownload, handleReprocess, handleDeleteClick]);

  // --- Column definitions for ResponsiveTable (Upload Files List) ---
  const uploadFilesColumns = useMemo(() => [
    {
      key: 'file.name',
      header: 'File Name',
      render: (_fileName: any, fileObj: FileObj) => {
        const fileExtension = fileObj.file.name.split('.').pop()?.toLowerCase() || '';
        const formatDetail = getFormatDetails(fileExtension);

        return (
          <div className="flex items-center space-x-3 flex-grow min-w-0">
            <FileIcon fileType={formatDetail.mime} />
            <div className="flex-grow min-w-0">
              {/* Applied truncateFilename and title for full filename on hover */}
              <p className="text-sm font-medium text-gray-900 truncate" title={fileObj.file.name}>{truncateFilename(fileObj.file.name)}</p>
              <p className="text-xs text-gray-500">{formatFileSize(fileObj.file.size)}</p>
              {fileObj.errors.length > 0 && (
                fileObj.errors.map((error, idx) => (
                  <p key={idx} className="text-xs text-error">{error}</p>
                ))
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'status', // This is a virtual key for rendering status/progress
      header: 'Status',
      render: (_value: any, fileObj: FileObj) => {
        const status = uploadStatus[fileObj.id];
        const progress = uploadProgress[fileObj.id];
        return (
          <div className="flex items-center space-x-2">
            {status === 'uploading' && (
              <span className="text-xs text-primary-500 flex items-center">
                <Loader2 className="w-3 h-3 animate-spin mr-1" /> Uploading: {progress || 0}%
              </span>
            )}
            {status === 'processing' && (
              <span className="text-xs text-yellow-500 flex items-center">
                <Loader2 className="w-3 h-3 animate-spin mr-1" /> Processing...
              </span>
            )}
            {status === 'ready' && (
              <span className="text-xs text-success flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" /> Complete
              </span>
            )}
            {status === 'failed' && (
              <span className="text-xs text-error flex items-center">
                <AlertTriangle className="w-3 h-3 mr-1" /> Failed
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_value: any, fileObj: FileObj) => {
        const status = uploadStatus[fileObj.id];
        return (
          <>
            {(!status || status === 'failed') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFile(fileObj.id)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </>
        );
      },
    },
  ], [uploadStatus, uploadProgress, removeFile]);


  return (
    <div className="p-6 space-y-9">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
        <Button onClick={() => setShowUploadSection(prev => !prev)} variant="primary" className="flex items-center">
          <Upload className="w-5 h-5 mr-2" />
          {showUploadSection ? 'Hide Upload' : 'Upload Document'}
        </Button>
      </div>

      {showUploadSection && (
        <Card className="mb-6 w-full">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload New Documents</h2>

          <FileUpload
            onFilesSelected={handleFilesSelected}
            onFileRemove={handleFileRemove}
            supportedFormats={Object.values(SUPPORTED_FORMATS).map(f => f.extension.toLowerCase())}
            maxFileSize={MAX_FILE_SIZE}
            allowMultiple={true}
            showFileList={false}
            disabled={uploading}
          />

          {selectedFiles.length > 0 && (
            <div className="mb-4 border border-gray-200 rounded-lg p-3 mt-4">
              <ResponsiveTable columns={uploadFilesColumns} data={selectedFiles} keyField="id" />
            </div>
          )}

          <div className="flex justify-end space-x-2 mt-6">
            <Button onClick={resetUploadSection} variant="secondary">
              Clear All
            </Button>
            <Button
              onClick={handleUploadAll}
              disabled={!allFilesValid || uploading || !hasFilesToUpload}
              loading={isAnyFileUploadingOrProcessing}
              variant="primary"
            >
              <Upload className="w-5 h-5 mr-2" />
              {isAnyFileUploadingOrProcessing ? 'Uploading...' : 'Upload Selected'}
            </Button>
          </div>
        </Card>
      )}

      {/* Modified the Card className for z-index */}
      <Card className="mb-6 w-full relative z-10">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search documents..."
              className="pl-10 pr-4 py-2 w-full"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <div className="flex gap-4 w-full md:w-auto justify-end">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                className="appearance-none border border-gray-300 rounded-lg pl-10 pr-8 py-2 focus:ring-2 focus:ring-primary-500 bg-white"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="all">All Statuses</option>
                <option value="processing">Processing</option>
                <option value="ready">Ready</option>
                <option value="failed">Failed</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 rounded-md mb-3"></div>
            ))}
          </div>
        ) : documents.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No documents yet"
            description="Upload your first document to start building your knowledge base."
            action={{
              label: "Upload Document",
              onClick: () => setShowUploadSection(true) // Open upload section
            }}
          />
        ) : (
          // Replaced the manual document list table with ResponsiveTable
          <ResponsiveTable columns={documentListColumns} data={documents} keyField="_id" />
        )}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-6">
                <Button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  variant="secondary"
                >
                  Previous
                </Button>
                <span className="text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <Button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  variant="secondary"
                >
                  Next
                </Button>
              </div>
            )}
      </Card>

      {selectedDocument && (
        <DocumentDetailsModal
          isOpen={!!selectedDocument}
          onClose={closeDocumentDetails}
          document={selectedDocument}
          onReprocess={handleReprocess}
          onDelete={handleDeleteClick}
          onDownload={handleDownload}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          document={documentToDelete}
        />
      )}
    </div>
  );
};

export default Documents;